import * as THREE from 'three'
import { POND_SIZE_USABLE } from '../const'
import fragmentShader from "@/Fish/main.frag"
import vertexShader from "@/Fish/main.vert"
import commonShader from "@/Fish/common.frag"
import { clamp } from 'three/src/math/MathUtils'
import { notes, triggerSound } from '@/music/Engine'

const LIGHT_UNDERWATER_FACTOR = 1.3
const LIGHT_RADIUS = 0.5 * LIGHT_UNDERWATER_FACTOR

export function getEdgeness(size: number, position: THREE.Vector3, direction: THREE.Vector2) {
    const s = size / 2

    const py = position.z
    const vy = direction.y

    const px = position.x
    const vx = direction.x

    const ty = ((Math.sign(direction.y) * s) - py) / vy
    const tx = ((Math.sign(direction.x) * s) - px) / vx

    if (vy === 0) {
        return clamp(tx, 0, 1)
    }
    if (vx === 0) {
        return clamp(ty, 0, 1)
    }

    return clamp(Math.min(ty, tx), 0, 1)
}

const FISH_SIZE = 0.5
const FISH_HEAD_SIZE = FISH_SIZE * 0.3
const FISH_DEFAULT_SPEED = 10
export class Fish extends THREE.Mesh {
    private shaderMaterial: THREE.ShaderMaterial
    private speed: number = FISH_DEFAULT_SPEED
    private rotationSpeed: number = Math.PI * 0.3
    private direction: THREE.Vector2 = new THREE.Vector2(1, 0)
    private distance: number = 0

    private lightenUp: boolean = false

    private note: string = notes[Math.floor(Math.random() * notes.length)]

    public isHovered = false

    public onClick: (fish: Fish) => void
    public onHover: (fish: Fish) => void

    constructor(scene: THREE.Scene, onClick: (fish: Fish) => void, onHover: (fish: Fish) => void, index: number) {

        const geo = new THREE.PlaneGeometry(FISH_SIZE, FISH_SIZE)
        const material = new THREE.RawShaderMaterial({
            vertexShader,
            fragmentShader: commonShader + fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uSpeed: { value: FISH_DEFAULT_SPEED },
                uDistance: { value: 0 },
                uIntersectPosition: { value: new THREE.Vector2(0, 0) },
                uIsGlowing: { value: + false },
                uIndex: { value: index }
            },
            side: THREE.DoubleSide,
            transparent: true
        })
        super(geo, material)
        this.onClick = onClick
        this.onHover = onHover
        const random = {
            x: Math.random() * POND_SIZE_USABLE,
            z: Math.random() * POND_SIZE_USABLE,
            defaultDirection: Math.random(),
            rotationSpeed: Math.random() - 0.5,
            speed: Math.random()
        }

        this.shaderMaterial = material

        // this.speed *= random.speed * 5
        this.rotationSpeed *= (random.rotationSpeed / 10)

        this.direction = this.direction.rotateAround({ x: 0, y: 0 }, random.defaultDirection * Math.PI * 2)

        this.rotation.set(-Math.PI / 2, 0, 0)
        this.position.set(random.x - POND_SIZE_USABLE / 2, 0.1 + random.x / 10, random.z - POND_SIZE_USABLE / 2)

        scene.add(this)

        const FISH_WIDTH = FISH_HEAD_SIZE / 2
        const FISH_HEIGHT = FISH_SIZE / 2
        this.geometry.boundingBox = new THREE.Box3(
            new THREE.Vector3(-FISH_WIDTH, -FISH_HEIGHT, 0),
            new THREE.Vector3(FISH_WIDTH, FISH_HEIGHT, 0)
        )
    }

    update(delta: number, elapsedTime: number, intersectPosition: THREE.Vector3) {

        const SPEED_MAX = 1
        const SPEED_MIN = 0.1
        const speedOffset = (Math.random() - 0.5) * 0.1

        this.speed += speedOffset
        this.speed = clamp(this.speed, SPEED_MIN, SPEED_MAX)


        const edgeness: number = getEdgeness(POND_SIZE_USABLE, this.position, this.direction)
        const speedEffective = this.speed * edgeness


        const ROTATION_SPEED_MAX = Math.PI * 0.01
        const ROTATION_SPEED_MIN = -ROTATION_SPEED_MAX
        this.rotationSpeed += ((Math.random() - 0.5) * Math.PI * 0.02)
        this.rotationSpeed = clamp(this.rotationSpeed, ROTATION_SPEED_MIN, ROTATION_SPEED_MAX)



        this.distance += speedEffective * delta * 3.0 + 0.01

        this.rotation.set(-Math.PI / 2, 0, 0)
        this.position.add(new THREE.Vector3(
            this.direction.x * speedEffective * delta,
            0.0,
            this.direction.y * speedEffective * delta
        ))
        this.rotateOnAxis(
            new THREE.Vector3(0, 0, 1),
            Math.PI + this.direction.angleTo(new THREE.Vector2(0, 1)) * Math.sign(this.direction.x)
        )

        this.shaderMaterial.uniforms.uTime.value = elapsedTime
        this.shaderMaterial.uniforms.uSpeed.value = speedEffective
        this.shaderMaterial.uniforms.uDistance.value = this.distance
        this.shaderMaterial.uniforms.uIntersectPosition.value = intersectPosition
        this.shaderMaterial.uniforms.uIsGlowing.value = + this.isHovered

        const rotationAngle = this.direction.angleTo(this.direction.clone().multiplyScalar(-1))
        const rotationSpeedEffective = edgeness <= 0.2
            ? Math.PI * 0.9 // not complete u-turn or it can get stuck in u-turn hell in corners  // (rotationAngle * (0.01 + (1 - edgeness) * 0.1))
            : this.rotationSpeed * speedEffective
        this.direction.rotateAround({ x: 0, y: 0 }, rotationSpeedEffective)


        const pointerDistance = Math.sqrt(Math.pow(this.position.x - intersectPosition.x, 2.0) + Math.pow(this.position.z - intersectPosition.z, 2.0))

        const isLitUp = pointerDistance < LIGHT_RADIUS

        if (isLitUp) {
            if (!this.lightenUp) triggerSound(this.note)
            this.lightenUp = true
        } else {
            this.lightenUp = false
        }

        this.isHovered = false
    }
}